<template>
    <div class="lot">
        <div class="lot__header">
            <div class="lot__header-left">
                <div class="lot__title">Лот №{{ auction.id }}</div>
                <div class="lot__status">{{ getAuctionStatusLabel(auction.status) }}</div>
            </div>

            <div class="lot__header-right">
                <div class="lot__header-block">
                    <div class="lot__header-label">Дата начала</div>
                    <div class="lot__header-value">{{ formatFromUTC(auction.start_date) }}</div>
                </div>

                <div class="lot__header-block">
                    <div class="lot__header-label">Дата окончания</div>
                    <div class="lot__header-value">{{ formatFromUTC(auction.end_date) }}</div>
                </div>
            </div>
        </div>

        <div class="lot__body">
            <div class="lot__body-left">
                <div>
                    <img
                        :src="auctionMainImage"
                        class="lot__image"
                    />
                </div>

                <div class="lot__body-info">
                    <div class="lot__body-name">{{ auctionCarFullName }}</div>
                    <div class="lot__body-description">{{ auctionCarSpecification }}</div>
                </div>
            </div>

            <div class="lot__body-center">
                <div class="lot__bid">
                    <div class="lot__bid-label">Всего ставок</div>
                    <div class="lot__bid-value">{{ auction.bids_count }}</div>
                </div>
            </div>

            <div class="lot__body-right">
                <router-link :to="{ name: 'BidReportDetail', params: { auction_id: auction.id } }">
                    <UISimpleButton
                        button-type="outlined"
                        class="lot__body-button"
                    >
                        Подробнее
                    </UISimpleButton>
                </router-link>
            </div>
        </div>
    </div>
</template>

<script lang="ts" setup>
    import noPhotoImage from '@/assets/images/cars/no-photo.webp';

    import { computed, defineProps } from 'vue';

    import { getAuctionStatusLabel } from '@/types/auction.type.ts';

    import {
        getCarEngineTypeLabel,
        getCarTransmissionTypeLabel,
        getCarDriveTypeLabel,
    } from '@/types/car.type.ts';
    import { ReportAuction } from '@/types/report.type.ts';

    import { formatFromUTC } from '@/utils/DateTimeFormatter.ts';
    import { numberFormat } from '@/utils/NumberFormatter.ts';

    import UISimpleButton from '@/components/ui/UISimpleButton/UISimpleButton.vue';

    const { auction } = defineProps<{
        auction: ReportAuction,
    }>();

    const auctionMainImage = computed<string>(() => {
        if (auction.car.photos.length > 0) {
            return auction.car.photos[0].path_small ?? auction.car.photos[0].path;
        }

        return noPhotoImage;
    });

    const auctionCarFullName = computed<string>(() => {
        return auction.car.brand_name
            + ' ' + auction.car.model_name
            + (auction.car.model_code_name ? (' ' + auction.car.model_code_name) : '')
            + ', ' + auction.car.production_year
            + '(' + auction.car.vin + ')';
    });

    const auctionCarSpecification = computed<string>(() => {
        const formatedEngineVolume = numberFormat(auction.car.engine_volume, { decimals: 1 });
        const formatedMileage = numberFormat(auction.car.mileage);
        const specificationItems: string[] = [
            `${formatedEngineVolume} (${auction.car.engine_power} л.с.)`,
            getCarEngineTypeLabel(auction.car.engine_type).toLowerCase(),
            getCarTransmissionTypeLabel(auction.car.transmission_type).toLowerCase(),
            `${formatedMileage} км`,
            getCarDriveTypeLabel(auction.car.drive_type).toLowerCase(),
        ];

        return specificationItems.join(', ');
    });
</script>

<style scoped lang="scss">
    @use 'LotItem' as *;
</style>
